import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"

import { cn } from "@/helpers/classNames"

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
)
Drawer.displayName = "Drawer"

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

function DrawerOverlay({
  className,
  ref,
  variant = "full",
  ...props
}: {
  ref?: React.Ref<React.ComponentRef<typeof DrawerPrimitive.Overlay>>
} & {
  variant?: "content" | "full"
} & React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed z-50 bg-black/60",
        variant === "full" ? "inset-0" : "top-[83px] right-0 bottom-0 left-72",
        className,
      )}
      {...props}
    />
  )
}

function DrawerContent({
  children,
  className,
  ref,
  variant = "full",
  ...props
}: {
  ref?: React.Ref<React.ComponentRef<typeof DrawerPrimitive.Content>>
} & {
  variant?: "content" | "full"
} & React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>) {
  return (
    <DrawerPortal>
      <DrawerOverlay variant={variant} />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          `
            fixed bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px]
            border bg-background
          `,
          variant === "full" ? "inset-x-0" : "right-0 left-72 mx-2",
          className,
        )}
        {...props}
      >
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
}

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        grid gap-1.5 p-4 text-center
        sm:text-left
      `,
      className,
    )}
    {...props}
  />
)
DrawerHeader.displayName = "DrawerHeader"

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
)
DrawerFooter.displayName = "DrawerFooter"

function DrawerTitle({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<React.ComponentRef<typeof DrawerPrimitive.Title>>
} & React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      ref={ref}
      className={cn(
        "text-lg leading-none font-semibold tracking-tight",
        className,
      )}
      {...props}
    />
  )
}

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
}
